import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../pages-styles/project-consultancy.css';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function Separation() {
  usePageMeta('Separation of Services - Magbity Agency Ltd', 'How our consultancy operates independently from the football agency to avoid conflicts of interest.');

  useEffect(() => {
    const el = document.querySelector('.sep-3d-card');
    if (el) el.classList.add('enter');
  }, []);

  return (
    <div className="project-consultancy">
      <section className="consultancy-hero-banner sep-hero">
        <div className="consultancy-hero-overlay"></div>
        <div className="consultancy-hero-content">
          <div className="consultancy-hero-text">
            <h1>Separation From Football Agency</h1>
            <p>
              Our consultancy services operate as an independent business unit. We maintain clear separation from any
              football agency activities to prevent conflicts of interest and ensure impartial advice.
            </p>
          </div>

          <div className="sep-illustration">
            <div className="sep-3d-scene">
              <div className="sep-3d-card">
                <div className="face front">
                  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                    <rect x="12" y="12" width="196" height="196" rx="14" fill="#f5f7fb" />
                    <g transform="translate(40,40)">
                      <rect x="0" y="0" width="120" height="60" rx="8" fill="#16c784" opacity="0.12" />
                      <rect x="0" y="70" width="120" height="30" rx="6" fill="#00d4ff" opacity="0.08" />
                    </g>
                  </svg>
                  <div className="card-label">Independent Units</div>
                </div>
                <div className="face back">
                  <h4>Clear Boundaries</h4>
                  <p>Separate teams, contracts and decision-making to avoid conflicts of interest.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultancy-services sep-details">
        <h2>How We Maintain Separation</h2>
        <div className="services-list">
          <div className="service-item left">
            <div className="service-content">
              <h3>Distinct Business Units</h3>
              <p>Consultancy and football agency operate under separate management and reporting lines.</p>
              <ul className="service-details-list">
                <li>Separate leadership and financials</li>
                <li>Independent operational processes</li>
                <li>Distinct budgets and KPIs</li>
                <li>Different contractual terms</li>
                <li>Clear escalation paths</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 13h8V3H3v10zm10 8h8V3h-8v18z" opacity=".2"/>
                <path d="M3 13h8V3H3v10zm10 8h8V3h-8v18z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2l4 4H8l4-4z" opacity=".2"/>
                <path d="M12 2l4 4H8l4-4z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Separate Teams & Expertise</h3>
              <p>Different staff groups handle consultancy and agency work to ensure impartiality.</p>
              <ul className="service-details-list">
                <li>Dedicated consultants and analysts</li>
                <li>Different recruitment and training</li>
                <li>No shared incentive structures</li>
                <li>Independent quality assurance</li>
                <li>Confidentiality boundaries enforced</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Separate Contracts & Agreements</h3>
              <p>Transactions are governed by separate legal agreements specific to each service.</p>
              <ul className="service-details-list">
                <li>Independent terms of engagement</li>
                <li>Clear scope and deliverables</li>
                <li>Separate invoicing and payment paths</li>
                <li>Conflict of interest clauses</li>
                <li>Non-overlapping confidentiality terms</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" opacity=".2"/>
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10l5 5 5-5H7z" opacity=".2"/>
                <path d="M7 10l5 5 5-5H7z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>No Cross-Benefit Guarantees</h3>
              <p>Success in one business arm does not translate to preferential treatment or guarantees in the other.</p>
              <ul className="service-details-list">
                <li>No shared client prioritisation</li>
                <li>Independent decision processes</li>
                <li>No preferential access to agency resources</li>
                <li>Transparent allocation of opportunities</li>
                <li>Separate performance reporting</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Independent Operations</h3>
              <p>Each unit manages its own operations, vendors, and external relationships.</p>
              <ul className="service-details-list">
                <li>Separate vendor selections</li>
                <li>Independent procurement processes</li>
                <li>Distinct project governance</li>
                <li>Independent reporting lines</li>
                <li>Separate risk registers</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18v2H3z" opacity=".2"/>
                <path d="M3 6h18v2H3z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4l8 8-8 8-8-8z" opacity=".2"/>
                <path d="M12 4l8 8-8 8-8-8z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Transparency & Disclosure</h3>
              <p>Where relevant, we disclose relationships and any potential overlaps to clients.</p>
              <ul className="service-details-list">
                <li>Full disclosure of material relationships</li>
                <li>Documented conflict management plans</li>
                <li>Client consent where overlaps may exist</li>
                <li>Independent oversight where needed</li>
                <li>Clear communication channels</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="responsibility-checklist">
        <h2>What Clients Should Expect</h2>
        <div className="checklist-grid">
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Clear Contractual Terms</h4>
            <p>Engagements will define scope, deliverables and responsibilities.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Independent Advice</h4>
            <p>Advice given in the best interest of the client's objectives.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Conflict Management</h4>
            <p>Processes to manage any potential conflicts will be enacted.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Separate Billing</h4>
            <p>Billing will be clear and separate for each service.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Independent KPIs</h4>
            <p>Performance measured against unit-specific KPIs.</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Open Communication</h4>
            <p>We keep clients informed and ensure transparent decision-making.</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Questions About Separation?</h2>
        <p>Contact us to discuss governance, conflicts of interest, or how we separate services operationally.</p>
        <Link to="/contact" className="cta-button">Contact Us</Link>
      </section>
    </div>
  );
}
